import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface OfferLetterData {
  date: string;
  internName: string;
  internAddressLine1: string;
  internCityStatePin: string;
  hrManagerName: string;
}

// Company Details
const COMPANY_DETAILS = {
  name: 'BOZ Meet Connect and Travel Pvt Ltd',
  address: 'Hustlehub Tech Park, 36/5, Somasundarapalya Main Rd, adjacent 27th Main Road, ITI Layout, HSR Layout, Bengaluru, Karnataka 560102',
  phone: '9591592804',
  email: 'support@bozapp.in',
  website: 'www.bozapp.in'
};

async function loadLogoAsBase64(): Promise<string | null> {
  try {
    const response = await fetch('/logo.png');
    if (!response.ok) {
      throw new Error('Logo not found');
    }
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.log('Could not load logo:', error);
    return null;
  }
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

export async function generateOfferLetterPDF(data: OfferLetterData): Promise<void> {
  // Escape user input to prevent XSS
  const safeDate = escapeHtml(data.date);
  const safeInternName = escapeHtml(data.internName);
  const safeInternAddressLine1 = escapeHtml(data.internAddressLine1);
  const safeInternCityStatePin = escapeHtml(data.internCityStatePin);
  const safeHrManagerName = escapeHtml(data.hrManagerName);
  
  // Load logo as base64 first
  const logoBase64 = await loadLogoAsBase64();
  const logoImgTag = logoBase64 
    ? `<img src="${logoBase64}" alt="BOZ logo" />`
    : '<div style="width: 84px; height: 84px; background: linear-gradient(180deg, #fff, #f3f7ff); border-radius: 12px; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 24px; color: #0b61b7;">BOZ</div>';
  
  // Create a temporary container for the offer letter HTML
  const tempContainer = document.createElement('div');
  tempContainer.style.position = 'absolute';
  tempContainer.style.left = '-9999px';
  tempContainer.style.width = '210mm'; // A4 width
  tempContainer.style.minHeight = '297mm'; // A4 height
  tempContainer.style.background = '#f4f6fb';
  tempContainer.innerHTML = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="utf-8">
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;600;700&display=swap" rel="stylesheet">
      <style>
        :root {
          --brand-blue: #0b61b7;
          --brand-dark: #073f78;
          --muted: #6b7280;
          --page-bg: #ffffff;
        }
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        /* A4 page dimensions: 210mm x 297mm */
        @page {
          size: A4;
          margin: 0;
        }
        
        body {
          font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, Arial;
          margin: 0;
          padding: 0;
          background: #f4f6fb;
          color: #111827;
        }
        
        /* Container sized for A4: 210mm width, accounting for margins */
        .container {
          width: 210mm; /* A4 width */
          height: 297mm; /* A4 height - fixed to fit one page */
          margin: 0 auto;
          background: var(--page-bg);
          padding: 12mm 12mm; /* Reduced padding to fit on one page */
          box-shadow: 0 8px 30px rgba(2, 6, 23, 0.06);
          border-radius: 10px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        
        header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 10px;
          flex-shrink: 0;
          margin-bottom: 4px;
        }
        
        .logo {
          width: 70px;
          height: 70px;
          background: linear-gradient(180deg, #fff, #f3f7ff);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid rgba(11, 97, 183, 0.08);
        }
        
        .logo img {
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
        }
        
        .company {
          font-weight: 700;
          color: var(--brand-dark);
          font-size: 16px;
        }
        
        .address {
          color: var(--muted);
          font-size: 11px;
          max-width: 540px;
          line-height: 1.4;
        }
        
        .accent {
          height: 4px;
          background: linear-gradient(90deg, var(--brand-blue), var(--brand-dark));
          border-radius: 4px;
          margin: 10px 0;
        }
        
        main {
          font-size: 13px;
          line-height: 1.5;
          color: #111827;
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        
        .meta {
          margin-bottom: 10px;
          color: var(--muted);
          font-size: 12px;
        }
        
        .section-title {
          font-weight: 700;
          margin-top: 8px;
          margin-bottom: 4px;
          color: var(--brand-dark);
          font-size: 13px;
        }
        
        .note {
          font-size: 11px;
          color: var(--muted);
        }
        
        .details {
          background: #fbfdff;
          border: 1px solid #eef6ff;
          padding: 8px;
          border-radius: 6px;
          margin-top: 4px;
          font-size: 12px;
        }
        
        .details strong {
          color: var(--brand-dark);
        }
        
        .bullets {
          margin-left: 16px;
          padding-left: 8px;
        }
        
        .bullets li {
          margin-bottom: 4px;
          font-size: 12px;
        }
        
        footer {
          margin-top: 8px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          color: var(--muted);
          font-size: 11px;
          flex-shrink: 0;
        }
        
        .signature {
          margin-top: 12px;
          flex-shrink: 0;
        }
        
        main p {
          margin-bottom: 6px;
          font-size: 12px;
        }
        
        .section {
          margin-bottom: 6px;
        }
        
        /* Print styles for A4 */
        @media print {
          body {
            background: transparent;
            padding: 0;
          }
          
          .container {
            box-shadow: none;
            border-radius: 0;
            margin: 0;
            width: 210mm;
            height: 297mm;
          }
        }
        
        /* Note: The container is sized for A4 (210mm x 297mm). 
           When generating PDF, selecting "A4" in print settings will ensure proper fit. */
      </style>
    </head>
    <body>
      <div class="container" role="document">
        <header>
          <div style="display: flex; gap: 14px; align-items: center;">
            <div class="logo">
              ${logoImgTag}
            </div>
            <div>
              <div class="company">${COMPANY_DETAILS.name}</div>
              <div class="address">${COMPANY_DETAILS.address}</div>
            </div>
          </div>
          <div style="text-align: right; color: var(--muted); font-size: 13px;">
            <div><strong>Phone:</strong> ${COMPANY_DETAILS.phone}</div>
            <div><strong>Email:</strong> ${COMPANY_DETAILS.email}</div>
            <div style="margin-top: 8px; font-weight: 700; color: var(--brand-blue);">${COMPANY_DETAILS.website}</div>
          </div>
        </header>

        <div class="accent" aria-hidden="true"></div>

        <main>
          <p class="meta">Date: ${safeDate}</p>

          <p>To,</p>
          <p><strong>${safeInternName}</strong><br>${safeInternAddressLine1}<br>${safeInternCityStatePin}</p>

          <p>Subject: <strong>Internship Offer — Trip Captain Intern (On-site, Paid)</strong></p>

          <p>Dear <strong>${safeInternName}</strong>,</p>

          <p>We are pleased to offer you the position of <strong>Trip Captain Intern</strong> at <strong>BOZ Meet Connect and Travel Pvt Ltd</strong>. This is a paid, on‑site internship based in Bengaluru. Below are the details of the internship and expectations from the role.</p>

          <div class="section">
            <div class="section-title">Internship Summary</div>
            <div class="details">
              <p><strong>Role:</strong> Trip Captain Intern (On‑site)</p>
              <p><strong>Location:</strong> Bengaluru (On‑site)</p>
              <p><strong>Stipend:</strong> ₹8,000 per month + ₹4,000 incentive</p>
              <p><strong>Duration:</strong> 6 months</p>
              <p><strong>Openings:</strong> 8</p>
              <p><strong>Work Schedule:</strong> 5‑day workweek with mandatory weekend shifts (Saturday & Sunday required as per trip schedules)</p>
            </div>
          </div>

          <div class="section">
            <div class="section-title">About BOZ</div>
            <p>BOZ Meet Connect and Travel Pvt Ltd is a leading travel company specializing in group travel experiences. We create opportunities for solo travellers to join curated group trips and make lasting memories and friendships while exploring new destinations. Our mission is to connect people through meaningful travel experiences.</p>
          </div>

          <div class="section">
            <div class="section-title">Your Responsibilities</div>
            <ul class="bullets">
              <li>Lead and manage group trips end‑to‑end, ensuring smooth operations from start to finish.</li>
              <li>Create memorable experiences by engaging with travellers and facilitating activities.</li>
              <li>Manage on‑ground logistics including accommodation, local transportation, and activity coordination.</li>
              <li>Act as the primary on‑site contact for guests and vendors during trips.</li>
              <li>Troubleshoot operational issues swiftly and maintain a calm, solution‑oriented approach.</li>
            </ul>
          </div>

          <div class="section">
            <div class="section-title">Why Join BOZ</div>
            <ul class="bullets">
              <li>Mentorship from experienced travel professionals.</li>
              <li>Competitive stipend of ₹8,000/month + ₹4,000 incentive.</li>
              <li>Direct path to a full‑time Trip Captain role upon successful completion of the internship and performance evaluation.</li>
              <li>Hands‑on experience leading trips and working in a fast‑paced travel environment.</li>
            </ul>
          </div>

          <div class="section">
            <div class="section-title">Eligibility & Traits We Value</div>
            <ul class="bullets">
              <li>Strong leadership and communication skills.</li>
              <li>Outgoing, people‑oriented personality with a passion for travel.</li>
              <li>Responsible, reliable, and able to make decisions under pressure.</li>
              <li>Prior experience in travel, hospitality, or leadership roles is a plus but not mandatory.</li>
            </ul>
          </div>

          <div class="section">
            <div class="section-title">Terms & Conditions</div>
            <p class="note">This offer is subject to the submission of required documents and a brief background verification (if applicable). During the internship, you are expected to adhere to company policies, maintain professional conduct, and protect confidential information. Any intellectual property created as part of your internship shall belong to BOZ.</p>
          </div>

          <div class="signature">
            <p>We're excited about the possibility of you joining the BOZ family and contributing to our mission of creating transformative travel experiences.</p>

            <p style="margin-top: 20px;">Warm regards,</p>
            <p style="font-weight: 700;">${safeHrManagerName}</p>
            <p style="color: var(--muted);">Human Resources, BOZ Meet Connect and Travel Pvt Ltd</p>
          </div>
        </main>

        <footer>
          <div style="color: var(--muted); font-size: 13px;">${COMPANY_DETAILS.name} — Hustlehub Tech Park, HSR Layout, Bengaluru</div>
          <div style="text-align: right; color: var(--muted); font-size: 13px;">Phone: ${COMPANY_DETAILS.phone} • Email: ${COMPANY_DETAILS.email}</div>
        </footer>
      </div>
    </body>
    </html>
  `;

  // Append to body temporarily
  document.body.appendChild(tempContainer);

  try {
    // Wait for fonts and images to load
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Get the container element
    const containerElement = tempContainer.querySelector('.container') as HTMLElement;
    if (!containerElement) {
      throw new Error('Could not find container element');
    }

    // Force container to show all content by ensuring proper height calculation
    // Wait a bit more for all content to be rendered
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Calculate A4 dimensions in pixels (at 96 DPI: 1mm = 3.7795px)
    const A4_WIDTH_MM = 210;
    const A4_HEIGHT_MM = 297;
    const MM_TO_PX = 3.7795;
    const a4WidthPx = A4_WIDTH_MM * MM_TO_PX; // ~794px
    const a4HeightPx = A4_HEIGHT_MM * MM_TO_PX; // ~1123px
    
    // Get the full height of the container
    const fullHeight = Math.max(
      containerElement.scrollHeight,
      containerElement.offsetHeight,
      containerElement.clientHeight
    );
    
    // Set explicit dimensions for A4 rendering - force single page
    const originalWidth = containerElement.style.width;
    const originalHeight = containerElement.style.height;
    containerElement.style.width = `${a4WidthPx}px`;
    containerElement.style.height = `${a4HeightPx}px`; // Fixed to A4 height for single page
    containerElement.style.overflow = 'hidden'; // Prevent overflow
    
    // Convert to canvas using html2canvas - fixed to A4 height for single page
    const canvas = await html2canvas(containerElement, {
      scale: 2,
      useCORS: true,
      allowTaint: false, // Keep false to avoid CORS issues
      logging: false,
      backgroundColor: '#f4f6fb',
      width: a4WidthPx,
      height: a4HeightPx, // Fixed to A4 height
      windowWidth: a4WidthPx,
      windowHeight: a4HeightPx,
      removeContainer: false,
      imageTimeout: 15000, // Increase timeout for image loading
    });
    
    // Ensure canvas is properly initialized
    if (!canvas || canvas.width === 0 || canvas.height === 0) {
      throw new Error('Failed to create canvas or canvas has zero dimensions');
    }

    // Restore original dimensions
    containerElement.style.width = originalWidth;
    containerElement.style.height = originalHeight;

    // Create PDF
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    // Calculate dimensions
    const imgWidth = 210; // A4 width in mm
    const imgHeight = 297; // A4 height in mm (fixed for single page)

    // Convert canvas to JPEG format (more reliable than PNG for jsPDF)
    // JPEG works better with jsPDF and avoids PNG signature issues
    const imgData = canvas.toDataURL('image/jpeg', 0.95);

    // Verify the data URL is valid
    if (!imgData || !imgData.startsWith('data:image/')) {
      throw new Error('Failed to convert canvas to image data');
    }

    // Add image to PDF - use JPEG format for reliability
    pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight, undefined, 'FAST');

    // Download PDF
    const fileName = `BOZ_OfferLetter_TripCaptainIntern_${data.internName.replace(/\s+/g, '_')}_${Date.now()}.pdf`;
    pdf.save(fileName);
  } finally {
    // Clean up
    document.body.removeChild(tempContainer);
  }
}

